/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { participantAPI } from '../../../api/participant';

export interface DeviceInfo {
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  hasAudioInputDevices: boolean;
  hasVideoInputDevices: boolean;
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
  };
}

/**
 *  This function will return 'true' when the specified permission has been denied by the user.
 *  If the API doesn't exist, or the query function returns an error, 'false' will be returned.
 */
export async function isPermissionDenied(name: 'camera' | 'microphone') {
  const permissionName = name as PermissionName; // workaround for https://github.com/microsoft/TypeScript/issues/33923

  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: permissionName });
      return result.state === 'denied';
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

/**
 * Send a log event to the backend
 */
export const logEvent = (level: string, message: string, preflight?: boolean, data?: any) => {
  const eventData = {
    preflight,
    browserTime: new Date(),
    level,
    message,
    data,
  };
  void participantAPI.rooms.sendBrowserLog(eventData);
};
